import React, { useState, useEffect } from "react";
import {
  Box,
  Button,
  useTheme,
  Typography,
  Paper,
  Grid,
  alpha,
  Stack,
  CircularProgress,
  TextField,
  MenuItem,
  Card,
  CardContent,
  Avatar,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
  Chip,
  FormControl,
  InputLabel,
  Select,
  InputAdornment,
  Autocomplete,
} from "@mui/material";
import {
  EmailOutlined as EmailOutlinedIcon,
  Phone as PhoneIcon,
  Person as PersonIcon,
  Send as SendIcon,
  ContactMail as ContactMailIcon,
  Business as BusinessIcon,
  Group as GroupIcon,
  Search as SearchIcon,
} from "@mui/icons-material";
import Header from "../../components/Header";
import { tokens } from "../../theme";
import axios from "axios";
import { API_BASE_URL } from "../../config/apiBase";

const USER_TYPE_FILTER_OPTIONS = [
  { value: "all", label: "All (Vehicle Owners & Customers)" },
  { value: "seller", label: "Vehicle Owners" },
  { value: "renter", label: "Customers" },
];

const ROLE_LABELS = {
  seller: "Vehicle Owner",
  renter: "Customer",
};

const getRoleLabel = (role) => ROLE_LABELS[role] || role;

const ContactCustomer = () => {
  const theme = useTheme();
  const baseColors = tokens(theme.palette.mode);

  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [sendingToAll, setSendingToAll] = useState(false);
  const [sellers, setSellers] = useState([]);
  const [renters, setRenters] = useState([]);
  const [filteredSellers, setFilteredSellers] = useState([]);
  const [filteredRenters, setFilteredRenters] = useState([]);
  const [selectedUserType, setSelectedUserType] = useState("all");
  const [selectedUser, setSelectedUser] = useState("");
  const [notificationDialog, setNotificationDialog] = useState(false);
  const [notificationTitle, setNotificationTitle] = useState("");
  const [alert, setAlert] = useState({ open: false, message: "", severity: "success" });
  const [searchTerm, setSearchTerm] = useState("");
  const [sendToAllRole, setSendToAllRole] = useState("renter");

  const colors = {
    ...baseColors,
    blueAccent: {
      100: "#d9eefb",
      200: "#b3ddf7",
      300: "#8dcbf4",
      400: "#67baf0",
      500: "#22a3f1",
      600: "#1b82c1",
      700: "#146291",
      800: "#0e4160",
      900: "#072030",
      50: "#e8f4fd",
    },
  };

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        // Fetch sellers data
        const sellersResponse = await axios.get(`${API_BASE_URL}/seller/getSellers`);
        if (sellersResponse.data && sellersResponse.data.success && sellersResponse.data.seller) {
          setSellers(sellersResponse.data.seller);
          setFilteredSellers(sellersResponse.data.seller);
        }

        // Fetch renters data
        const rentersResponse = await axios.get(`${API_BASE_URL}/renter/getAllRenters`);
        if (rentersResponse.data && rentersResponse.data.success && rentersResponse.data.renters) {
          setRenters(rentersResponse.data.renters);
          setFilteredRenters(rentersResponse.data.renters);
        }
      } catch (error) {
        console.error("Error fetching data:", error);
        showAlert("Error fetching customer data", "error");
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  // Filter users based on search term
  useEffect(() => {
    const filterUsers = () => {
      if (!searchTerm.trim()) {
        setFilteredSellers(sellers);
        setFilteredRenters(renters);
        return;
      }

      const term = searchTerm.toLowerCase().trim();
      
      const filteredS = sellers.filter(seller => 
        seller.firstName?.toLowerCase().includes(term) ||
        seller.lastName?.toLowerCase().includes(term) ||
        seller.email?.toLowerCase().includes(term) ||
        seller.contactNumber?.includes(term) ||
        seller.location?.toLowerCase().includes(term)
      );
      
      const filteredR = renters.filter(renter => 
        renter.firstName?.toLowerCase().includes(term) ||
        renter.lastName?.toLowerCase().includes(term) ||
        renter.email?.toLowerCase().includes(term) ||
        renter.contactNumber?.includes(term) ||
        renter.location?.toLowerCase().includes(term)
      );

      setFilteredSellers(filteredS);
      setFilteredRenters(filteredR);
    };

    filterUsers();
  }, [searchTerm, sellers, renters]);

  const showAlert = (message, severity = "success") => {
    setAlert({ open: true, message, severity });
    setTimeout(() => setAlert({ open: false, message: "", severity: "success" }), 4000);
  };

  const parseSelectedUserKey = (key) => {
    if (!key || !key.includes(":")) return null;
    const [role, id] = key.split(":");
    if (!role || !id) return null;
    const allUsers = [
      ...sellers.map((s) => ({ ...s, role: "seller" })),
      ...renters.map((r) => ({ ...r, role: "renter" })),
    ];
    return allUsers.find(
      (u) => u.role === role && u.id.toString() === id.toString()
    );
  };

  const handleSendNotification = async () => {
    if (!selectedUser || !notificationTitle.trim()) {
      showAlert("Please select a user and enter a title", "error");
      return;
    }

    setSending(true);
    try {
      const user = parseSelectedUserKey(selectedUser);

      if (!user) {
        showAlert("Selected user not found", "error");
        setSending(false);
        return;
      }

      const payload = {
        user_id: parseInt(user.id, 10),
        user_role: user.role,
        title: notificationTitle.trim(),
      };

      const response = await axios.post(
        `${API_BASE_URL}/admin/send-notification`,
        payload,
        {
          headers: {
            "Content-Type": "application/json",
          },
        }
      );

      if (response.data && response.data.success) {
        const pushSent = response.data.result?.result?.push;
        const inAppOnly = response.data.result?.result?.inApp && !pushSent;
        const detail = inAppOnly
          ? " (saved in-app; user has no push token on device)"
          : pushSent
            ? " (push sent)"
            : "";
        showAlert(
          `Notification sent to ${user.firstName} ${user.lastName}!${detail}`,
          inAppOnly ? "warning" : "success"
        );
        setNotificationDialog(false);
        setNotificationTitle("");
        setSelectedUser("");
        setSelectedUserType("all");
      } else {
        showAlert(response.data.message || "Failed to send notification", "error");
      }
    } catch (error) {
      console.error("Error sending notification:", error);
      if (error.response) {
        showAlert(
          `Error: ${error.response.data.error || error.response.data.message || "Failed to send notification"}`,
          "error"
        );
      } else if (error.request) {
        showAlert("Network error: Unable to reach server", "error");
      } else {
        showAlert("Error sending notification", "error");
      }
    } finally {
      setSending(false);
    }
  };

  const handleSendToAll = async () => {
    if (!notificationTitle.trim()) {
      showAlert("Please enter a notification title", "error");
      return;
    }

    setSendingToAll(true);
    try {
      const payload = {
        user_role: sendToAllRole,  // "seller" or "renter"
        title: notificationTitle.trim(),
      };

      console.log("Sending notification to all with payload:", payload);

      const response = await axios.post(
        `${API_BASE_URL}/admin/send-notification-to-all`, 
        payload,
        {
          headers: {
            'Content-Type': 'application/json',
          }
        }
      );
      
      console.log("Response:", response.data);
      
      if (response.data && response.data.success) {
        const userType = sendToAllRole === "seller" ? "vehicle owners" : "customers";
        showAlert(`Notification sent successfully to all ${userType}!`, "success");
        setNotificationDialog(false);
        setNotificationTitle("");
      } else {
        showAlert(response.data.message || "Failed to send notification to all users", "error");
      }
    } catch (error) {
      console.error("Error sending notification to all:", error);
      if (error.response) {
        console.error("Error response:", error.response.data);
        showAlert(`Error: ${error.response.data.error || error.response.data.message || "Failed to send notification"}`, "error");
      } else if (error.request) {
        showAlert("Network error: Unable to reach server", "error");
      } else {
        showAlert("Error sending notification", "error");
      }
    } finally {
      setSendingToAll(false);
    }
  };

  const openNotificationDialog = (sendToAll = false) => {
    if (sendToAll) {
      setSelectedUser("");
      setNotificationDialog(true);
      return;
    }
    if (!selectedUser) {
      showAlert("Please select a user first", "warning");
      return;
    }
    setNotificationDialog(true);
  };

  const getFilteredUsers = () => {
    const allUsers = [
      ...filteredSellers.map(seller => ({ ...seller, role: "seller", type: "seller" })),
      ...filteredRenters.map(renter => ({ ...renter, role: "renter", type: "renter" }))
    ];

    if (selectedUserType === "all") {
      return allUsers;
    }
    return allUsers.filter(user => user.type === selectedUserType);
  };

  const UserCard = ({ user, type }) => (
    <Card
      sx={{
        p: 2,
        backgroundColor: "#ffffff",
        boxShadow: `0 10px 30px rgba(0, 0, 0, 0.05)`,
        border: `1px solid #e0e0e0`,
        borderRadius: 2,
        transition: "all 0.3s ease",
        height: "100%",
        "&:hover": {
          transform: "translateY(-5px)",
          boxShadow: `0 15px 35px rgba(0, 0, 0, 0.1)`,
        },
      }}
    >
      <CardContent>
        <Stack direction="row" spacing={2} alignItems="flex-start">
          <Avatar
            src={user.imageURL}
            sx={{
              width: 60,
              height: 60,
              backgroundColor: type === "seller" ? colors.blueAccent[100] : colors.blueAccent[200],
            }}
          >
            <PersonIcon />
          </Avatar>
          
          <Box sx={{ flex: 1 }}>
            <Stack direction="row" justifyContent="space-between" alignItems="flex-start" mb={1}>
              <Typography variant="h6" fontWeight="bold" color="#333333">
                {user.firstName} {user.lastName}
              </Typography>
              <Chip
                label={getRoleLabel(type)}
                size="small"
                sx={{
                  backgroundColor: type === "seller" ? colors.blueAccent[500] : colors.blueAccent[600],
                  color: "white",
                  fontWeight: "bold",
                  textTransform: "capitalize",
                }}
              />
            </Stack>

            <Stack spacing={1}>
              <Stack direction="row" spacing={1} alignItems="center">
                <EmailOutlinedIcon sx={{ fontSize: 16, color: colors.blueAccent[500] }} />
                <Typography variant="body2" color="#666666">
                  {user.email}
                </Typography>
              </Stack>

              <Stack direction="row" spacing={1} alignItems="center">
                <PhoneIcon sx={{ fontSize: 16, color: colors.blueAccent[500] }} />
                <Typography variant="body2" color="#666666">
                  {user.contactNumber}
                </Typography>
              </Stack>

              {user.location && (
                <Stack direction="row" spacing={1} alignItems="center">
                  <BusinessIcon sx={{ fontSize: 16, color: colors.blueAccent[500] }} />
                  <Typography variant="body2" color="#666666">
                    {user.location}
                  </Typography>
                </Stack>
              )}
            </Stack>

            <Button
              variant="outlined"
              startIcon={<SendIcon />}
              onClick={() => {
                setSelectedUser(`${type}:${user.id}`);
                setSelectedUserType(type);
                setNotificationDialog(true);
              }}
              sx={{
                mt: 2,
                borderColor: colors.blueAccent[500],
                color: colors.blueAccent[500],
                "&:hover": {
                  borderColor: colors.blueAccent[600],
                  backgroundColor: alpha(colors.blueAccent[500], 0.04),
                },
                width: "100%",
              }}
            >
              Send Notification
            </Button>
          </Box>
        </Stack>
      </CardContent>
    </Card>
  );

  const filteredUsers = getFilteredUsers().map((user) => ({
    ...user,
    optionKey: `${user.role}:${user.id}`,
    label: `${user.firstName} ${user.lastName} (${getRoleLabel(user.role)}) - ${user.email}`,
  }));

  const selectedUserDetails = parseSelectedUserKey(selectedUser);
  const selectedUserOption =
    filteredUsers.find((u) => u.optionKey === selectedUser) || null;

  return (
    <Box m="20px" height="80vh" overflow="auto" paddingRight="20px">
      {/* Header Section */}
      <Box
        mb={3}
        p={3}
        borderRadius={2}
        sx={{
          background: `linear-gradient(90deg, #22a3f1 0%, #67baf0 100%)`,
          boxShadow: `0 4px 12px rgba(0, 0, 0, 0.1)`,
          border: `1px solid rgba(255, 255, 255, 0.3)`,
        }}
      >
        <Box display="flex" alignItems="center" gap={1} mb={1}>
          <ContactMailIcon sx={{ color: "white", fontSize: 28 }} />
          <Typography variant="h3" fontWeight="bold" color="white">
            Contact Customers
          </Typography>
        </Box>
        <Typography variant="body1" color="white">
          Send notifications to vehicle owners and customers
        </Typography>
      </Box>

      {/* Alert */}
      {alert.open && (
        <Alert severity={alert.severity} sx={{ mb: 3 }} onClose={() => setAlert({ ...alert, open: false })}>
          {alert.message}
        </Alert>
      )}

      {/* Search Section */}
      <Paper
        sx={{
          p: 3,
          backgroundColor: "#ffffff",
          boxShadow: `0 10px 30px rgba(0, 0, 0, 0.05)`,
          border: `1px solid #e0e0e0`,
          borderRadius: 2,
          mb: 3,
        }}
      >
        <Stack direction="row" alignItems="center" spacing={1} mb={2}>
          <SearchIcon sx={{ color: colors.blueAccent[500], fontSize: 28 }} />
          <Typography variant="h4" fontWeight="bold" color={colors.blueAccent[700]}>
            Search Users
          </Typography>
        </Stack>
        
        <TextField
          fullWidth
          variant="outlined"
          placeholder="Search by name, email, phone, or location..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon sx={{ color: colors.blueAccent[500] }} />
              </InputAdornment>
            ),
          }}
          sx={{
            "& .MuiOutlinedInput-root": {
              borderRadius: 2,
              transition: "all 0.3s ease",
              "&:hover": {
                borderColor: colors.blueAccent[500],
              },
              "&.Mui-focused": {
                borderColor: colors.blueAccent[500],
                boxShadow: `0 0 0 2px ${alpha(colors.blueAccent[500], 0.2)}`,
              },
            },
          }}
        />
        
        {searchTerm && (
          <Typography variant="body2" color="#666666" mt={1}>
            Found {filteredSellers.length + filteredRenters.length} users matching "{searchTerm}"
          </Typography>
        )}
      </Paper>

      {/* Send Notification Section */}
      <Paper
        sx={{
          p: 3,
          backgroundColor: "#ffffff",
          boxShadow: `0 10px 30px rgba(0, 0, 0, 0.05)`,
          border: `1px solid #e0e0e0`,
          borderRadius: 2,
          mb: 3,
        }}
      >
        <Stack direction="row" alignItems="center" spacing={1} mb={3}>
          <SendIcon sx={{ color: colors.blueAccent[500], fontSize: 28 }} />
          <Typography variant="h4" fontWeight="bold" color={colors.blueAccent[700]}>
            Send Notification
          </Typography>
        </Stack>

        <Grid container spacing={2} alignItems="end">
          <Grid item xs={12} md={3}>
            <FormControl fullWidth>
              <InputLabel>Audience</InputLabel>
              <Select
                value={selectedUserType}
                label="Audience"
                onChange={(e) => {
                  setSelectedUserType(e.target.value);
                  setSelectedUser("");
                }}
              >
                {USER_TYPE_FILTER_OPTIONS.map((opt) => (
                  <MenuItem key={opt.value} value={opt.value}>
                    {opt.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>

          <Grid item xs={12} md={4}>
            <Autocomplete
              options={filteredUsers}
              value={selectedUserOption}
              onChange={(_, newValue) => {
                setSelectedUser(newValue ? newValue.optionKey : "");
                if (newValue?.role) {
                  setSelectedUserType(newValue.role);
                }
              }}
              getOptionLabel={(option) => option.label || ""}
              isOptionEqualToValue={(option, value) =>
                option.optionKey === value.optionKey
              }
              noOptionsText="No users found"
              disabled={filteredUsers.length === 0}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Select User"
                  placeholder="Type name, email, or phone..."
                />
              )}
            />
          </Grid>

          <Grid item xs={12} md={2}>
            <Button
              variant="contained"
              startIcon={<SendIcon />}
              onClick={() => openNotificationDialog(false)}
              disabled={!selectedUser}
              sx={{
                backgroundColor: colors.blueAccent[500],
                "&:hover": {
                  backgroundColor: colors.blueAccent[600],
                },
                "&:disabled": {
                  backgroundColor: colors.blueAccent[200],
                },
                width: "100%",
                height: "56px",
              }}
            >
              Send to User
            </Button>
          </Grid>

          <Grid item xs={12} md={3}>
            <Box sx={{ display: 'flex', gap: 1 }}>
              <FormControl fullWidth>
                <InputLabel>Send to All</InputLabel>
                <Select
                  value={sendToAllRole}
                  label="Send to All"
                  onChange={(e) => setSendToAllRole(e.target.value)}
                >
                  <MenuItem value="renter">All Customers</MenuItem>
                  <MenuItem value="seller">All Vehicle Owners</MenuItem>
                </Select>
              </FormControl>
              <Button
                variant="outlined"
                startIcon={<GroupIcon />}
                onClick={() => openNotificationDialog(true)}
                sx={{
                  borderColor: colors.blueAccent[500],
                  color: colors.blueAccent[500],
                  "&:hover": {
                    borderColor: colors.blueAccent[600],
                    backgroundColor: alpha(colors.blueAccent[500], 0.04),
                  },
                  minWidth: '120px',
                  height: "56px",
                }}
              >
                Send to All
              </Button>
            </Box>
          </Grid>
        </Grid>

        {selectedUserDetails && (
          <Box mt={2} p={2} sx={{ backgroundColor: colors.blueAccent[50], borderRadius: 1 }}>
            <Typography variant="body2" fontWeight="bold">
              Selected: {selectedUserDetails.firstName} {selectedUserDetails.lastName} ({getRoleLabel(selectedUserDetails.role)})
            </Typography>
            <Typography variant="body2" color="#666666">
              Email: {selectedUserDetails.email} | Phone: {selectedUserDetails.contactNumber}
            </Typography>
          </Box>
        )}
      </Paper>

      {loading ? (
        <Box display="flex" justifyContent="center" my={4}>
          <CircularProgress sx={{ color: colors.blueAccent[500] }} />
        </Box>
      ) : (
        <Grid container spacing={3}>
          {/* Sellers Section */}
          <Grid item xs={12} md={6}>
            <Paper
              sx={{
                p: 3,
                backgroundColor: "#ffffff",
                boxShadow: `0 10px 30px rgba(0, 0, 0, 0.05)`,
                border: `1px solid #e0e0e0`,
                borderRadius: 2,
                mb: 3,
              }}
            >
              <Stack direction="row" alignItems="center" justifyContent="space-between" mb={3}>
                <Stack direction="row" alignItems="center" spacing={1}>
                  <BusinessIcon sx={{ color: colors.blueAccent[500], fontSize: 28 }} />
                  <Typography variant="h4" fontWeight="bold" color={colors.blueAccent[700]}>
                    Vehicle Owners ({filteredSellers.length})
                  </Typography>
                </Stack>
                <Chip 
                  label={`${filteredSellers.length} users`} 
                  sx={{ backgroundColor: colors.blueAccent[500], color: "white" }}
                />
              </Stack>

              <Grid container spacing={2}>
                {filteredSellers.length > 0 ? (
                  filteredSellers.map((seller) => (
                    <Grid item xs={12} key={seller.id}>
                      <UserCard user={seller} type="seller" />
                    </Grid>
                  ))
                ) : (
                  <Grid item xs={12}>
                    <Typography variant="body1" color="#666666" textAlign="center" py={2}>
                      {searchTerm ? "No vehicle owners found matching your search" : "No vehicle owners found"}
                    </Typography>
                  </Grid>
                )}
              </Grid>
            </Paper>
          </Grid>

          {/* Renters Section */}
          <Grid item xs={12} md={6}>
            <Paper
              sx={{
                p: 3,
                backgroundColor: "#ffffff",
                boxShadow: `0 10px 30px rgba(0, 0, 0, 0.05)`,
                border: `1px solid #e0e0e0`,
                borderRadius: 2,
                mb: 3,
              }}
            >
              <Stack direction="row" alignItems="center" justifyContent="space-between" mb={3}>
                <Stack direction="row" alignItems="center" spacing={1}>
                  <PersonIcon sx={{ color: colors.blueAccent[500], fontSize: 28 }} />
                  <Typography variant="h4" fontWeight="bold" color={colors.blueAccent[700]}>
                    Customers ({filteredRenters.length})
                  </Typography>
                </Stack>
                <Chip 
                  label={`${filteredRenters.length} users`} 
                  sx={{ backgroundColor: colors.blueAccent[600], color: "white" }}
                />
              </Stack>

              <Grid container spacing={2}>
                {filteredRenters.length > 0 ? (
                  filteredRenters.map((renter) => (
                    <Grid item xs={12} key={renter.id}>
                      <UserCard user={renter} type="renter" />
                    </Grid>
                  ))
                ) : (
                  <Grid item xs={12}>
                    <Typography variant="body1" color="#666666" textAlign="center" py={2}>
                      {searchTerm ? "No customers found matching your search" : "No customers found"}
                    </Typography>
                  </Grid>
                )}
              </Grid>
            </Paper>
          </Grid>
        </Grid>
      )}

      {/* Send Notification Dialog */}
      <Dialog
        open={notificationDialog}
        onClose={() => !sending && !sendingToAll && setNotificationDialog(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          <Stack direction="row" alignItems="center" spacing={1}>
            <SendIcon sx={{ color: colors.blueAccent[500] }} />
            <Typography variant="h6" fontWeight="bold">
              {selectedUser ? "Send Notification to User" : "Send Notification to All"}
            </Typography>
          </Stack>
        </DialogTitle>
        <DialogContent>
          {selectedUserDetails ? (
            <Box mb={2} p={2} sx={{ backgroundColor: colors.blueAccent[50], borderRadius: 1 }}>
              <Typography variant="body2" fontWeight="bold">
                To: {selectedUserDetails.firstName} {selectedUserDetails.lastName} ({getRoleLabel(selectedUserDetails.role)})
              </Typography>
              <Typography variant="body2" color="#666666">
                Email: {selectedUserDetails.email}
              </Typography>
              <Typography variant="body2" color="#666666">
                Phone: {selectedUserDetails.contactNumber}
              </Typography>
            </Box>
          ) : (
            <Box mb={2} p={2} sx={{ backgroundColor: colors.blueAccent[50], borderRadius: 1 }}>
              <Typography variant="body2" fontWeight="bold">
                Sending to All {sendToAllRole === "seller" ? "Vehicle Owners" : "Customers"}
              </Typography>
              <Typography variant="body2" color="#666666">
                This notification will be sent to all {sendToAllRole === "seller" ? filteredSellers.length : filteredRenters.length}{" "}
                {sendToAllRole === "seller" ? "vehicle owners" : "customers"}
              </Typography>
            </Box>
          )}
          
          <TextField
            fullWidth
            label="Notification Title *"
            value={notificationTitle}
            onChange={(e) => setNotificationTitle(e.target.value)}
            margin="normal"
            variant="outlined"
            placeholder="Enter notification title"
            helperText="This will be sent as the notification title to the selected user(s)"
            autoFocus
            multiline
            rows={3}
            sx={{
              "& .MuiOutlinedInput-root": {
                borderRadius: 2,
                transition: "all 0.3s ease",
                "&:hover": {
                  borderColor: colors.blueAccent[500],
                },
                "&.Mui-focused": {
                  borderColor: colors.blueAccent[500],
                  boxShadow: `0 0 0 2px ${alpha(colors.blueAccent[500], 0.2)}`,
                },
              },
            }}
          />
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button
            onClick={() => {
              setNotificationDialog(false);
              setNotificationTitle("");
            }}
            disabled={sending || sendingToAll}
            sx={{ color: colors.blueAccent[600] }}
          >
            Cancel
          </Button>
          <Button
            onClick={selectedUser ? handleSendNotification : handleSendToAll}
            disabled={sending || sendingToAll || !notificationTitle.trim()}
            variant="contained"
            startIcon={sending || sendingToAll ? null : <SendIcon />}
            sx={{
              backgroundColor: colors.blueAccent[500],
              "&:hover": {
                backgroundColor: colors.blueAccent[600],
              },
              "&:disabled": {
                backgroundColor: colors.blueAccent[200],
              },
            }}
          >
            {sending || sendingToAll ? (
              <CircularProgress size={24} color="inherit" />
            ) : selectedUser ? (
              "Send to User"
            ) : (
              "Send to All"
            )}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ContactCustomer;